/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { InputBase, styled } from '@mui/material';
import theme from '../../../../Shared/Themes/theme';
import { StyleConstants } from '../../../../Shared/Constants/Style.Constants';

export const PaginationStyle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
}));

export const ModifyButtonContainer = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}));

export const ButtonContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '1rem',
}));

export const ModifyScheduleText = styled('span')(() => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h2?.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '1.5rem',
  letterSpacing: StyleConstants.LetterSpacingTight,
  marginBottom: '0.5rem',
}));

export const ScheduleDaysText = styled('span')(() => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h4?.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '1.5rem',
  letterSpacing: StyleConstants.LetterSpacingTight,
  display: 'flex',
  marginBottom: '0.75rem',
}));

export const wrapperStyles = {
  daysStyle: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '3rem',
    height: '3rem',
    border: '1px solid #006B6B',
    borderRadius: '50%',
    fontFamily: theme.typography.fontFamily,
    lineHeight: '1.5rem',
    fontSize: theme.typography.h5?.fontSize,
  },
  frequencyOptions: {
    borderRadius: '1rem',
    width: '100%',
    border: '1px solid #006B6B',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.typography.fontFamily,
    cursor: 'pointer',
  },
  manageUserIcons: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem',
    alignItems: 'flex-end',
    marginTop: '1rem',
    width: '100%',
    justifyContent: 'flex-end',
  },
  userRemoveContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.45rem',
    gap: '0.25rem',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    borderRadius: '0.75rem',
    alignItems: 'center',
  },
  textField: {
    minWidth: '5rem',
    width: '100%',
    '& .MuiInputBase-root': {
      paddingRight: '0.75rem !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset,&:hover fieldset,&.Mui-focused fieldset': {
        borderWidth: 1,
        borderColor: theme.palette.secondary.light,
        borderRadius: '1rem',
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.h4?.fontSize,
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightRegular,
      lineHeight: '1.5rem',
      letterSpacing: '0.00375rem',
      '&.Mui-focused': {
        color: theme.palette.text.secondary,
        fontSize: theme.typography.h5?.fontSize,
      },
    },
    '@media (max-width: 64rem)': {
      '& .MuiInputLabel-root': {
        fontSize: '.875rem',
        lineHeight: '1.375rem',
        letterSpacing: StyleConstants.LetterSpacingDefault,
        '&.Mui-focused': {
          fontSize: theme.typography.h6?.fontSize,
        },
      },
    },
  },
  radioButton: {
    width: '1.25rem',
    height: '1.25rem',
    color: theme.palette.common.black,
    '&.Mui-checked': {
      color: theme.palette.common.black,
      backgroundColor: theme.palette.primary.main,
      '& + span:last-child': {
        fontWeight: theme.typography.fontWeightBold,
      },
    },
  },
  radioButtonDisabled: {
    width: '1.25rem',
    height: '1.25rem',
    color: theme.palette.common.black,
    '&.Mui-checked': {
      color: theme.palette.common.black,
      backgroundColor: theme.palette.primary.main,
      opacity: 0.3,
      '& + span:last-child': {
        fontWeight: theme.typography.fontWeightBold,
      },
    },
  },
  radioButtonText: {
    fontFamily: theme.typography.fontFamily,
    marginRight: '0 !important',
    marginLeft: '0',
    gap: '0.5rem',
    '&.MuiFormControlLabel-root': {
      '&> span:last-child': {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.h2?.fontSize,
        fontWeight: theme.typography.fontWeightLight,
        lineHeight: '1.375rem',
        letterSpacing: StyleConstants.LetterSpacingTight,
        color: theme.palette.text.primary,
      },
    },
  },
  textStyle: {
    '& .MuiFormControlLabel-label': {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.h2?.fontSize,
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  dayCircleStyle: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `0.0625rem solid ${theme.palette.primary.dark}`,
    cursor: 'pointer',
  },
  quarterBoxStyle: {
    gap: '0.75rem',
    display: 'flex',
    padding: '0.5rem 0',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  quarterlyBoxStyle: {
    gap: '0.75rem',
    display: 'flex',
    padding: '0.5rem 0',
    marginBottom: '1rem',
  },
};

export const NoteText = styled('span')(() => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h4?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '1.5rem',
  letterSpacing: StyleConstants.LetterSpacingTight,
  marginBottom: '0.75rem',
}));

export const EditableOptionContainer = styled('div')(() => ({
  gap: '1rem',
  display: 'flex',
  marginBottom: '1rem',
  flexDirection: 'row',
}));

export const ScheduleDaysContainer = styled('div')(() => ({
  gap: '0.3rem',
  marginBottom: '0.5rem',
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
}));

export const CreateUserButtonContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '1rem',
  gap: '1rem',
}));

export const TasksContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3,1fr)',
  gap: '1.5rem',
  [theme.breakpoints.down('laptop')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
  },
}));

export const HeadingContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '1rem',
  backgroundColor: theme.palette.secondary.customColor7,
  borderTopLeftRadius: '1rem',
  borderTopRightRadius: '1rem',
}));

export const TextContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

export const UpdateButtonContainer = styled('div')(() => ({
  position: 'fixed',
  right: '4rem',
  bottom: '3rem',
  zIndex: 1000,
}));

export const searchFieldStyles = (searchText: string) => ({
  marginBottom: '1rem',
  border: searchText ? `0.0625rem solid ${theme.palette.primary.dark}` : '',
});

export const ModifyContainer = styled('div')(() => ({
  maxHeight: '70vh',
  overflowY: 'auto',
  paddingRight: '0.75rem',
}));

export const ClosureDateContainer = styled('div')(() => ({
  marginBottom: '2rem',
  display: 'flex',
  flex: 1,
}));

export const SelectActionInput = styled(InputBase)(({ theme }) => ({
  '& .MuiSelect-select': {
    border: `0.0625rem solid ${theme.palette.primary.dark}`,
    borderRadius: '1rem',
    color: theme.palette.primary.dark,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h4.fontSize,
    width: '7.25rem',
    '&:focus': {
      borderRadius: '1rem',
    },
  },
  '&> div': {
    padding: '1.0625rem 2rem',
  },
  [theme.breakpoints.down('laptop')]: {
    '& .MuiSelect-select': {
      fontSize: theme.typography.h5?.fontSize,
      width: '6.25rem',
    },
    '&> div': {
      padding: '0.75rem 1.5rem',
    },
  },
}));

export const ColumnHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const FilterWrapper = styled('div')(() => ({
  borderRadius: '1.5rem',
  boxShadow: '0px 2px 5px rgba(0,0,0,0.1)',
  overflowY: 'auto',
  paddingTop: '1rem',
}));

export const SearchFieldWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  borderRadius: '1rem',
  width: '90%',
  flexDirection: 'row',
  paddingRight: '1rem',
  border: `0.0625rem solid ${theme.palette.divider}`,
}));

export const LabelsWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.25rem',
  padding: '1rem 1.5rem 0.5rem',
  maxHeight: '5rem',
  overflowY: 'auto',
}));

export const filterStyles = {
  labelStyles: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.secondary.customColor16,
    borderRadius: '1rem',
    padding: '0.25rem 0.5rem',
    fontSize: '0.85rem',
  },
  loaderStyles: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
  },
};

export const ApplyFilterWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.5rem',
}));

export const JobName = styled('h4')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h4?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '1.5rem',
  letterSpacing: StyleConstants.LetterSpacingTight,
  color: theme.palette.text.primary,
  [theme.breakpoints.down('laptop')]: {
    fontSize: theme.typography.h5?.fontSize,
    lineHeight: '1.375rem',
  },
}));

export const PersonName = styled('h3')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: '1.375rem',
  letterSpacing: StyleConstants.LetterSpacingTight,
  marginBottom: '2rem',
  [theme.breakpoints.down('laptop')]: {
    fontSize: theme.typography.h4.fontSize,
    lineHeight: '1.5rem',
  },
}));

export const VendorTextStyling = styled('h3')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h4.fontSize,
  fontWeight: theme.typography.fontWeightLight,
  lineHeight: '1.375rem',
  letterSpacing: StyleConstants.LetterSpacingDefault,
  [theme.breakpoints.down('laptop')]: {
    fontSize: theme.typography.h6.fontSize,
    lineHeight: '1rem',
    letterSpacing: '0.025rem',
  },
}));
